import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Heart, Activity, Info } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './TargetHeartRateCalculatorPage/components/CalculationForm';
import ResultsOverview from './TargetHeartRateCalculatorPage/components/ResultsOverview';
import TargetZonesVisualization from './TargetHeartRateCalculatorPage/components/TargetZonesVisualization';
import QuickAdjustmentSidebar from './TargetHeartRateCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './TargetHeartRateCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './TargetHeartRateCalculatorPage/components/TipsSidebar';
import InfoContent from './TargetHeartRateCalculatorPage/components/InfoContent';
import { calculateHeartRateZones } from '../../../utils/calculations/health';

// Определение начального состояния формы
export const initialFormData = {
  age: '30',
  gender: 'male',
  restingHeartRate: '70',
  fitnessLevel: 'average', // 'beginner', 'average', 'athletic', 'professional'
  exerciseType: 'cardio', // 'cardio', 'weight_loss', 'endurance', 'recovery'
  healthCondition: 'healthy', // 'healthy', 'hypertension', 'heart_disease', 'diabetes'
  calculationMethod: 'tanaka', // 'tanaka', 'hf', 'nes', 'gellish'
  includeRestingHR: true,
};

// Определение методов расчета максимального пульса
export const maxHRMethods = [
  { id: 'tanaka', name: 'Танака (208 - 0.7 × возраст)', description: 'Современная формула, рекомендованная большинством специалистов' },
  { id: 'hf', name: 'Классическая (220 - возраст)', description: 'Традиционная формула, простая и широко используемая' },
  { id: 'nes', name: 'Норв. унив. науки (211 - 0.64 × возраст)', description: 'Разработана Норвежским университетом науки и технологий' },
  { id: 'gellish', name: 'Геллиш (207 - 0.7 × возраст)', description: 'Формула для более точного расчета у спортсменов' },
];

// Определение типов тренировок
export const exerciseTypes = [
  { id: 'cardio', name: 'Кардиотренировка', defaultZones: { lower: 70, upper: 85 } },
  { id: 'weight_loss', name: 'Снижение веса', defaultZones: { lower: 60, upper: 70 } },
  { id: 'endurance', name: 'Развитие выносливости', defaultZones: { lower: 75, upper: 85 } },
  { id: 'recovery', name: 'Восстановление', defaultZones: { lower: 50, upper: 60 } },
  { id: 'interval', name: 'Интервальная тренировка', defaultZones: { lower: 65, upper: 90 } },
  { id: 'strength', name: 'Силовая тренировка', defaultZones: { lower: 70, upper: 80 } },
];

// Определение описаний для уровней физической подготовки
export const fitnessLevels = [
  { id: 'beginner', name: 'Начинающий', adjustment: -5 },
  { id: 'average', name: 'Средний', adjustment: 0 },
  { id: 'athletic', name: 'Атлетический', adjustment: 5 },
  { id: 'professional', name: 'Профессиональный', adjustment: 10 },
];

// Определение состояний здоровья и корректировок
export const healthConditions = [
  { id: 'healthy', name: 'Здоров(а)', adjustment: 0, description: 'Без хронических заболеваний' },
  { id: 'hypertension', name: 'Гипертония', adjustment: -10, description: 'Повышенное артериальное давление' },
  { id: 'heart_disease', name: 'Сердечные заболевания', adjustment: -15, description: 'Различные сердечно-сосудистые заболевания' },
  { id: 'diabetes', name: 'Диабет', adjustment: -5, description: 'Сахарный диабет 1 или 2 типа' },
  { id: 'asthma', name: 'Астма', adjustment: -5, description: 'Бронхиальная астма' },
  { id: 'overweight', name: 'Избыточный вес', adjustment: -5, description: 'ИМТ более 30' },
];

/**
 * TargetHeartRateCalculatorPage Component - Полный калькулятор целевого пульса с различными методами расчета и визуализацией.
 */
const TargetHeartRateCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('heart-rate-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор целевого пульса (THR) | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте ваши целевые зоны пульса для эффективных тренировок с учетом возраста, пола, уровня подготовки и состояния здоровья. Точный калькулятор для определения оптимальных пульсовых зон для различных типов нагрузок.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedProfiles, setSavedProfiles] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных профилей из localStorage ---
  useEffect(() => {
    const savedProfilesData = localStorage.getItem('savedHeartRateProfiles');
    if (savedProfilesData) {
      try {
        setSavedProfiles(JSON.parse(savedProfilesData));
      } catch (e) {
        console.error("Error loading saved profiles:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findExerciseType = (id) => {
        const found = exerciseTypes.find(t => t.id === id);
        return found ? found.id : initialFormData.exerciseType;
      };

      switch (subToolId) {
        case 'training-zones':
          newState.exerciseType = 'cardio';
          break;
        case 'max-heart-rate':
          newState.calculationMethod = 'tanaka';
          break;
        case 'fat-burning':
          newState.exerciseType = 'weight_loss';
          break;
        case 'recovery':
          newState.exerciseType = 'recovery';
          break;
        case 'endurance':
          newState.exerciseType = 'endurance';
          break;
        case 'interval':
          newState.exerciseType = 'interval';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение профиля в localStorage
  const saveProfile = (name) => {
    if (!results) return;

    const newSavedProfile = {
      id: Date.now().toString(),
      name: name || `Профиль: ${formData.age} лет, ${formData.gender === 'male' ? 'муж' : 'жен'}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedProfiles = [...savedProfiles, newSavedProfile];
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedHeartRateProfiles', JSON.stringify(updatedSavedProfiles));

    return newSavedProfile;
  };

  // Удаление сохраненного профиля
  const deleteSavedProfile = (id) => {
    const updatedSavedProfiles = savedProfiles.filter(profile => profile.id !== id);
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedHeartRateProfiles', JSON.stringify(updatedSavedProfiles));
  };

  // Загрузка сохраненного профиля
  const loadSavedProfile = (id) => {
    const profileToLoad = savedProfiles.find(profile => profile.id === id);
    if (profileToLoad) {
      setFormData(profileToLoad.formData);
      setResults(profileToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Heart className="w-8 h-8 text-red-600 dark:text-red-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор целевого пульса (THR)'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              maxHRMethods={maxHRMethods}
              exerciseTypes={exerciseTypes}
              fitnessLevels={fitnessLevels}
              healthConditions={healthConditions}
              saveProfile={saveProfile}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <TargetZonesVisualization 
                  results={results}
                  exerciseType={formData.exerciseType}
                  exerciseTypes={exerciseTypes}
                />
              </>
            )}

            {/* --- Информационный контент о пульсе --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              exerciseTypes={exerciseTypes}
            />

            {/* --- Панель сравнения профилей --- */}
            <ComparisonSidebar 
              savedProfiles={savedProfiles}
              deleteSavedProfile={deleteSavedProfile}
              loadSavedProfile={loadSavedProfile}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Charts */
        .heart-rate-chart {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .heart-rate-chart {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .heart-rate-chart::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .heart-rate-chart::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .heart-rate-chart::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .heart-rate-chart::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .heart-rate-chart::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .heart-rate-chart::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .heart-rate-chart::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default TargetHeartRateCalculatorPage;